import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';
import '../styles/PageProducts.css';

function PageProducts() {
	const [products, setProducts] = useState([]);
	const [categories, setCategories] = useState([]);

	// search
	const [searchQuery, setSearchQuery] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');

	// category (ID) & subCategory (name)
	const [categoryFilter, setCategoryFilter] = useState('');
	const [subCategoryFilter, setSubCategoryFilter] = useState('');

	// price
	const [minPrice, setMinPrice] = useState('');
	const [maxPrice, setMaxPrice] = useState('');

	// availability & colors
	const [availableFilter, setAvailableFilter] = useState(false);
	const [colorFilters, setColorFilters] = useState([]);

	// dimensions / weight
	const [minWidth, setMinWidth] = useState('');
	const [maxWidth, setMaxWidth] = useState('');
	const [minHeight, setMinHeight] = useState('');
	const [maxHeight, setMaxHeight] = useState('');
	const [minLength, setMinLength] = useState('');
	const [maxLength, setMaxLength] = useState('');
	const [minThickness, setMinThickness] = useState('');
	const [maxThickness, setMaxThickness] = useState('');
	const [minWeight, setMinWeight] = useState('');
	const [maxWeight, setMaxWeight] = useState('');

	// sort
	const [sortOption, setSortOption] = useState('');

	// mobile
	const [filtersOpen, setFiltersOpen] = useState(false);
	const [expandedSections, setExpandedSections] = useState({
		colors: false,
		dimensions: false
	});

	const location = useLocation();
	const navigate = useNavigate();

	/* ================= FETCH ================= */
	useEffect(() => {
		fetch(`${process.env.REACT_APP_API_URL}/api/products`)
			.then(res => res.json())
			.then(setProducts)
			.catch(console.error);

		fetch(`${process.env.REACT_APP_API_URL}/api/categories`)
			.then(res => res.json())
			.then(setCategories)
			.catch(console.error);
	}, []);

	/* ================= DEBOUNCE SEARCH ================= */
	useEffect(() => {
		const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
		return () => clearTimeout(t);
	}, [searchQuery]);

	/* ================= URL → STATE ================= */
	useEffect(() => {
		const p = new URLSearchParams(location.search);

		setCategoryFilter(p.get('category') || '');
		setSubCategoryFilter(p.get('subCategory') || '');
		setMinPrice(p.get('minPrice') || '');
		setMaxPrice(p.get('maxPrice') || '');
		setAvailableFilter(p.get('available') === '1');

		setMinWidth(p.get('minWidth') || '');
		setMaxWidth(p.get('maxWidth') || '');
		setMinHeight(p.get('minHeight') || '');
		setMaxHeight(p.get('maxHeight') || '');
		setMinLength(p.get('minLength') || '');
		setMaxLength(p.get('maxLength') || '');
		setMinThickness(p.get('minThickness') || '');
		setMaxThickness(p.get('maxThickness') || '');
		setMinWeight(p.get('minWeight') || '');
		setMaxWeight(p.get('maxWeight') || '');

		const colors = p.get('colors');
		setColorFilters(colors ? colors.split(',') : []);

		setSortOption(p.get('sort') || '');
	}, [location.search]);

	/* ================= STATE → URL ================= */
	useEffect(() => {
		const p = new URLSearchParams();

		if (categoryFilter) p.set('category', categoryFilter);
		if (subCategoryFilter) p.set('subCategory', subCategoryFilter);
		if (colorFilters.length) p.set('colors', colorFilters.join(','));
		if (availableFilter) p.set('available', '1');

		if (minPrice) p.set('minPrice', minPrice);
		if (maxPrice) p.set('maxPrice', maxPrice);

		if (minWidth) p.set('minWidth', minWidth);
		if (maxWidth) p.set('maxWidth', maxWidth);
		if (minHeight) p.set('minHeight', minHeight);
		if (maxHeight) p.set('maxHeight', maxHeight);
		if (minLength) p.set('minLength', minLength);
		if (maxLength) p.set('maxLength', maxLength);
		if (minThickness) p.set('minThickness', minThickness);
		if (maxThickness) p.set('maxThickness', maxThickness);
		if (minWeight) p.set('minWeight', minWeight);
		if (maxWeight) p.set('maxWeight', maxWeight);

		if (sortOption) p.set('sort', sortOption);

		navigate({ search: p.toString() }, { replace: true });
	}, [
		categoryFilter,
		subCategoryFilter,
		colorFilters,
		availableFilter,
		minPrice,
		maxPrice,
		minWidth,
		maxWidth,
		minHeight,
		maxHeight,
		minLength,
		maxLength,
		minThickness,
		maxThickness,
		minWeight,
		maxWeight,
		sortOption,
		navigate
	]);

	/* ================= FILTERING ================= */
	const filteredProducts = useMemo(() => {
		let list = products.filter(p =>
			p.name.toLowerCase().includes(debouncedSearch.toLowerCase())
		);

		if (categoryFilter)
			list = list.filter(p => 
				p.categories?.some(cat => cat.category === categoryFilter)
			);

		if (subCategoryFilter)
			list = list.filter(p =>
				p.categories?.some(cat => cat.subCategory === subCategoryFilter)
			);

		if (availableFilter)
			list = list.filter(p => p.stock > 0);

		if (colorFilters.length)
			list = list.filter(p =>
				colorFilters.some(c => p.colors?.includes(c))
			);

		if (minPrice) list = list.filter(p => p.price >= Number(minPrice));
		if (maxPrice) list = list.filter(p => p.price <= Number(maxPrice));

		if (minWidth) list = list.filter(p => p.width >= Number(minWidth));
		if (maxWidth) list = list.filter(p => p.width <= Number(maxWidth));
		if (minHeight) list = list.filter(p => p.height >= Number(minHeight));
		if (maxHeight) list = list.filter(p => p.height <= Number(maxHeight));
		if (minLength) list = list.filter(p => p.length >= Number(minLength));
		if (maxLength) list = list.filter(p => p.length <= Number(maxLength));
		if (minThickness) list = list.filter(p => p.thickness >= Number(minThickness));
		if (maxThickness) list = list.filter(p => p.thickness <= Number(maxThickness));
		if (minWeight) list = list.filter(p => p.weight >= Number(minWeight));
		if (maxWeight) list = list.filter(p => p.weight <= Number(maxWeight));

		if (sortOption === 'priceAsc') list.sort((a, b) => a.price - b.price);
		if (sortOption === 'priceDesc') list.sort((a, b) => b.price - a.price);
		if (sortOption === 'createdAtDesc')
			list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
		if (sortOption === 'createdAtAsc')
			list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

		return list;
	}, [
		products,
		debouncedSearch,
		categoryFilter,
		subCategoryFilter,
		availableFilter,
		colorFilters,
		minPrice,
		maxPrice,
		minWidth,
		maxWidth,
		minHeight,
		maxHeight,
		minLength,
		maxLength,
		minThickness,
		maxThickness,
		minWeight,
		maxWeight,
		sortOption
	]);

	/* ================= HELPERS ================= */
	const activeCategory = categories.find(c => c._id === categoryFilter);
	const subCategories = activeCategory?.subcategories || [];
	const colors = [...new Set(products.flatMap(p => p.colors || []))];

	const toggleColor = color => {
		setColorFilters(prev =>
			prev.includes(color)
				? prev.filter(c => c !== color)
				: [...prev, color]
		);
	};

	const toggleSection = section => {
		setExpandedSections(prev => ({
			...prev,
			[section]: !prev[section]
		}));
	};

	const clearFilters = () => {
		setSearchQuery('');
		setCategoryFilter('');
		setSubCategoryFilter('');
		setColorFilters([]);
		setAvailableFilter(false);
		setMinPrice('');
		setMaxPrice('');
		setMinWidth('');
		setMaxWidth('');
		setMinHeight('');
		setMaxHeight('');
		setMinLength('');
		setMaxLength('');
		setMinThickness('');
		setMaxThickness('');
		setMinWeight('');
		setMaxWeight('');
		setSortOption('');
		navigate({ search: '' });
	};

	/* ================= RENDER ================= */
	return (
		<div className="Products">
			<button className={`filters-toggle-button ${filtersOpen ? 'open' : ''}`} onClick={() => setFiltersOpen(v => !v)}>
				{filtersOpen ? '✕' : '☰ Filtry'}
			</button>

			<aside className={`filters-container ${filtersOpen ? 'open' : ''}`}>
				<div className="filters-bar">

					<input
						type="text"
						placeholder="🔍 Szukaj produktu"
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
					/>

					<h4>Kategoria</h4>
					<select
						value={categoryFilter}
						onChange={e => {
							setCategoryFilter(e.target.value);
							setSubCategoryFilter('');
						}}
					>
						<option value="">Wszystkie</option>
						{categories.map(c => (
							<option key={c._id} value={c._id}>{c.name}</option>
						))}
					</select>

					<select
						value={subCategoryFilter}
						disabled={!categoryFilter}
						onChange={e => setSubCategoryFilter(e.target.value)}
					>
						<option value="">Podkategoria</option>
						{subCategories.map(sc => (
							<option key={sc.name} value={sc.name}>{sc.name}</option>
						))}
					</select>

					<h4>Cena</h4>
					<div className="range-inputs">
						<input placeholder="min" value={minPrice} onChange={e => setMinPrice(e.target.value)} />
						<input placeholder="max" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} />
					</div>

					<label className="checkbox">
						<input type="checkbox" checked={availableFilter} onChange={e => setAvailableFilter(e.target.checked)} />
						Tylko dostępne
					</label>

					<div className="filter-section">
						<h4 className="section-header" onClick={() => toggleSection('colors')}>
							Kolory
							<span className={`section-arrow ${expandedSections.colors ? 'open' : ''}`}>▼</span>
						</h4>
						{expandedSections.colors && (
							<div className="section-content">
								{colors.map(c => (
									<label key={c} className="checkbox">
										<input
											type="checkbox"
											checked={colorFilters.includes(c)}
											onChange={() => toggleColor(c)}
										/>
										{c}
									</label>
								))}
							</div>
						)}
					</div>

					<div className="filter-section">
						<h4 className="section-header" onClick={() => toggleSection('dimensions')}>
							Wymiary / Waga
							<span className={`section-arrow ${expandedSections.dimensions ? 'open' : ''}`}>▼</span>
						</h4>
						{expandedSections.dimensions && (
							<div className="section-content">
								{[
									['Szerokość', minWidth, setMinWidth, maxWidth, setMaxWidth],
									['Wysokość', minHeight, setMinHeight, maxHeight, setMaxHeight],
									['Długość', minLength, setMinLength, maxLength, setMaxLength],
									['Grubość', minThickness, setMinThickness, maxThickness, setMaxThickness],
									['Waga', minWeight, setMinWeight, maxWeight, setMaxWeight],
								].map(([label, min, setMin, max, setMax]) => (
									<div className="filter-group" key={label}>
										<span>{label}</span>
										<div className="range-inputs">
											<input placeholder="min" value={min} onChange={e => setMin(e.target.value)} />
											<input placeholder="max" value={max} onChange={e => setMax(e.target.value)} />
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					<h4>Sortuj</h4>
					<select value={sortOption} onChange={e => setSortOption(e.target.value)}>
						<option value="">Domyślnie</option>
						<option value="priceAsc">Cena ↑</option>
						<option value="priceDesc">Cena ↓</option>
						<option value="createdAtDesc">Najnowsze</option>
						<option value="createdAtAsc">Najstarsze</option>
					</select>

					<button className="clear-filters" onClick={clearFilters}>
						Wyczyść filtry
					</button>
				</div>
			</aside>
<main className='products-content'>
	<p className="results-count">
		Znaleziono: {filteredProducts.length}
	</p>

	<div className="products-container">
		{filteredProducts.map(p => (
			<ProductCard key={p._id} product={p} />
		))}
	</div>
</main>

		</div>
	);
}

export default PageProducts;
